import React, { useState, useEffect } from 'react';
import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
import { Droppable } from '@hello-pangea/dnd';
import Typography from '@mui/material/Typography';

import localData from 'utils/localData';
import { IssueStatus, getIssueStatus } from 'shared/constants/issues';

import Issue from './Issue';
import { List, Title, IssuesCount, Issues, ExtraItemsBlock } from './Styles';
import { ProjectProptypes } from 'shared/propTypes/projectType';
import { FilterProptypes } from 'shared/propTypes/filterType';
import { USER_COMMENT } from 'shared/constants/comment';
import MDLoadingButton from 'components/MDLoadingButton';
import { filterIssues, getSortedListIssues } from '../utils';

const propTypes = {
  status: PropTypes.string.isRequired,
  project: PropTypes.shape(ProjectProptypes).isRequired,
  filters: PropTypes.shape(FilterProptypes).isRequired,
  fetchProject: PropTypes.func.isRequired,
};

const ProjectBoardList = ({ status, project, fetchProject, filters }) => {
  const [isLoading, setIsLoading] = useState(false);

  const getMoreIssuesStatusDone = async () => {
    setIsLoading(true);
    fetchProject({
      params: {
        organizationId: localData.get(USER_COMMENT.CURRENT_ORGID),
        dateThres: project.dateThres,
        fetchMoreDoneIssues: true,
      },
    });
  };

  // Use useEffect to monitor the project prop and stop loading when it updates
  useEffect(() => {
    // Whenever project updates, set isLoading to false
    setIsLoading(false);
  }, [project]); // The effect runs when the 'project' prop updates

  const formatIssuesCount = (allListIssues, filteredListIssues) => {
    if (allListIssues.length !== filteredListIssues.length) {
      return `${filteredListIssues.length} of ${allListIssues.length}`;
    }
    return allListIssues.length;
  };

  const filteredIssues = filterIssues(project.issues, filters);
  const filteredListIssues = getSortedListIssues(filteredIssues, status);
  const allListIssues = getSortedListIssues(project.issues, status);

  return (
    <Droppable
      key={status}
      droppableId={status}
      data-testid={`board-list-droppable:${status}`}>
      {(provided) => (
        <List>
          <Title>
            {`${getIssueStatus(status)} `}
            <IssuesCount>
              {formatIssuesCount(allListIssues, filteredListIssues)}
            </IssuesCount>
          </Title>
          <Issues
            {...provided.droppableProps}
            ref={provided.innerRef}
            data-testid={`board-list:${status}`}>
            {filteredListIssues.map((issue, index) => (
              <Issue
                key={issue.id}
                projectUsers={project.users}
                issue={issue}
                index={index}
              />
            ))}
            {provided.placeholder}

            {status === IssueStatus.DONE && (
              <ExtraItemsBlock>
                {project.noMoreDoneIssues ? (
                  <Typography
                    style={{
                      opacity: 0.6,
                      fontSize: '1rem',
                    }}>
                    {intl.get('kanban_done_column_text_loaded_all')}
                  </Typography>
                ) : (
                  <MDLoadingButton
                    circular={true}
                    variant="contained"
                    color="tertiary"
                    loading={isLoading}
                    loadingPosition="start"
                    onClick={getMoreIssuesStatusDone}>
                    {intl.get('kanban_button_load_more')}
                  </MDLoadingButton>
                )}
              </ExtraItemsBlock>
            )}
          </Issues>
        </List>
      )}
    </Droppable>
  );
};

ProjectBoardList.propTypes = propTypes;

export default ProjectBoardList;
